import styled, { css } from 'styled-components';

/**
 * Building blocks of the video flow (docs/video-flow-design/VIDEO_FLOW_DESIGN.html): the glass panel, labels, pills,
 * label/value pairs, buttons and progress bars. Colours come from the theme where it has them.
 */

export const flow = {
	page: '#121412',
	panel: 'rgba(28, 30, 28, 0.86)',
	panelBorder: 'rgba(70, 74, 70, 0.75)',
	rule: 'rgba(80, 84, 80, 0.6)',
	text: '#e9ece9',
	text2: '#b4b9b4',
	text3: '#858a85',
	greenFill: 'rgba(32, 172, 107, 0.16)',
	warn: '#e0a030',
	error: '#e25a4a',
	radius: '16px',
};

export const panelCss = css`
	background: ${flow.panel};
	border: 1px solid ${flow.panelBorder};
	border-radius: ${flow.radius};
	backdrop-filter: blur(14px);
`;

export const FlowPanel = styled.section`
	${panelCss};
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
	padding: 1.125rem 1.25rem;
	flex: none;
	color: ${flow.text};
	${({ $fill }) =>
		$fill &&
		css`
			flex: 1 0 auto;
			min-height: 0;
		`}
`;

export const PanelTitle = styled.h2`
	margin: 0;
	font-size: 1.19rem;
	font-weight: 700;
	color: ${flow.text};
	overflow-wrap: anywhere;
`;

export const SectionTitle = styled.h3`
	margin: 0;
	font-size: 0.85rem;
	font-weight: 600;
	color: ${flow.text};
`;

export const Label = styled.span`
	font-size: 0.69rem;
	letter-spacing: 0.9px;
	text-transform: uppercase;
	color: ${flow.text3};
	font-weight: 600;
`;

export const Muted = styled.p`
	margin: 0;
	color: ${flow.text2};
	font-size: 0.82rem;
	line-height: 1.45;
`;

export const Small = styled.div`
	color: ${flow.text3};
	font-size: 0.72rem;
	line-height: 1.4;
`;

export const Rule = styled.hr`
	border: 0;
	border-top: 1px solid ${flow.rule};
	margin: 0;
	opacity: 1;
	width: 100%;
`;

export const Pills = styled.div`
	display: flex;
	gap: 0.375rem;
	flex-wrap: wrap;
`;

export const Pill = styled.span`
	align-self: flex-start;
	border-radius: 999px;
	padding: 0.25rem 0.7rem;
	font-size: 0.75rem;
	font-weight: 600;
	${({ $variant, theme }) =>
		$variant === 'ghost'
			? css`
					color: ${flow.text2};
					border: 1px solid #3a3e3a;
				`
			: css`
					background: ${theme.colors.darkGreen};
					color: #fff;
					border: 1px solid ${theme.colors.darkGreen};
				`}
`;

/** Two-column grid of label + value pairs. Pass [[label, value], ...]; empty values are left out. */
const KvGrid = styled.dl`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem 0.625rem;
	margin: 0;
	dd {
		margin: 0.2rem 0 0;
		font-size: 0.82rem;
		font-weight: 600;
		color: ${flow.text};
		overflow-wrap: anywhere;
	}
`;

export const KeyValues = ({ items }) => (
	<KvGrid>
		{items
			.filter(([, v]) => v != null && v !== '')
			.map(([k, v]) => (
				<div key={k}>
					<dt>
						<Label>{k}</Label>
					</dt>
					<dd>{v}</dd>
				</div>
			))}
	</KvGrid>
);

export const FlowButton = styled.button`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	padding: 0.7rem 0.9rem;
	border-radius: 11px;
	font-weight: 600;
	font-size: 0.875rem;
	cursor: pointer;
	${({ $variant, theme }) =>
		$variant === 'secondary'
			? css`
					background: transparent;
					color: ${flow.text2};
					border: 1px solid #3a3e3a;
					&:hover:not(:disabled) {
						border-color: ${theme.colors.green};
						color: ${flow.text};
					}
				`
			: css`
					background: ${theme.colors.darkGreen};
					color: #fff;
					border: 1px solid ${theme.colors.darkGreen};
					&:hover:not(:disabled) {
						background: ${theme.colors.green};
					}
				`}
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 2px;
	}
`;

export const ButtonRow = styled.div`
	display: grid;
	grid-template-columns: ${({ $single }) => ($single ? '1fr' : '1fr 2fr')};
	gap: 0.625rem;
	margin-top: auto;
`;

const BarTrack = styled.div`
	height: 6px;
	border-radius: 3px;
	background: #2f332f;
	overflow: hidden;
	i {
		display: block;
		height: 100%;
		border-radius: 3px;
		background: ${({ $tone, theme }) => ($tone === 'warn' ? flow.warn : theme.colors.green)};
		transition: width 0.4s ease;
	}
`;

export const ProgressBar = ({ value, tone, label }) => (
	<BarTrack
		$tone={tone}
		role="progressbar"
		aria-label={label}
		aria-valuemin={0}
		aria-valuemax={100}
		aria-valuenow={Math.round(value)}
	>
		<i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
	</BarTrack>
);

export const SpreadLine = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 0.75rem;
	font-size: 0.78rem;
	color: ${flow.text2};
	font-variant-numeric: tabular-nums;
`;

export const Note = styled.div`
	border-radius: 12px;
	padding: 0.75rem 0.875rem;
	font-size: 0.78rem;
	line-height: 1.45;
	${({ $tone }) =>
		$tone === 'error'
			? css`
					background: rgba(226, 90, 74, 0.1);
					border: 1px solid rgba(226, 90, 74, 0.4);
					color: #f1c2bb;
				`
			: $tone === 'ok'
				? css`
						background: rgba(32, 172, 107, 0.1);
						border: 1px solid rgba(32, 172, 107, 0.4);
						color: #bfe6d1;
					`
				: css`
						background: rgba(224, 160, 48, 0.1);
						border: 1px solid rgba(224, 160, 48, 0.35);
						color: #ecd3a5;
					`}
`;

export const Foot = styled.div`
	margin-top: auto;
	font-size: 0.72rem;
	color: ${flow.text3};
	line-height: 1.6;
	border-top: 1px solid ${flow.rule};
	padding-top: 0.625rem;
	overflow-wrap: anywhere;
`;

/** The strip under the timeline: a panel that fills what is left of the centre column. */
export const UnderPanel = styled.div`
	${panelCss};
	flex: 1;
	min-height: 96px;
	padding: 0.75rem 1.125rem 0.875rem;
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

export const UnderHead = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 1rem;
`;

const SwitchButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	flex: none;
	border: 0;
	background: transparent;
	color: ${flow.text2};
	font-size: 0.78rem;
	padding: 0;
	cursor: pointer;
	i {
		position: relative;
		width: 32px;
		height: 18px;
		border-radius: 9px;
		background: ${({ $on, theme }) => ($on ? theme.colors.darkGreen : '#3a3e3a')};
		transition: background 0.2s;
	}
	i::after {
		content: '';
		position: absolute;
		top: 2px;
		left: ${({ $on }) => ($on ? '16px' : '2px')};
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #fff;
		transition: left 0.2s;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 3px;
		border-radius: 4px;
	}
`;

/** A labelled on/off switch, used for the Detections / Line toggles on the timeline. */
export const Switch = ({ label, on, onChange }) => (
	<SwitchButton
		type="button"
		role="switch"
		aria-checked={on}
		$on={on}
		onClick={() => onChange(!on)}
	>
		{label}
		<i aria-hidden="true" />
	</SwitchButton>
);
